/**
 * Responsive Utilities for Mobile-First Development
 * Provides breakpoints, hooks, and class utilities for responsive design
 */

// Breakpoints matching Tailwind config
export const breakpoints = {
  xs: 320,   // Extra small (mobile)
  sm: 640,   // Small (landscape mobile)
  md: 768,   // Medium (tablet)
  lg: 1024,  // Large (desktop)
  xl: 1280,  // Extra large (wide desktop)
  '2xl': 1536, // 2x extra large
};

/**
 * Get breakpoint name for current width
 * @param {number} width - Window width
 * @returns {string} Breakpoint name (xs, sm, md, lg, xl, 2xl)
 */
export const getBreakpointName = (width) => {
  if (width >= breakpoints['2xl']) return '2xl';
  if (width >= breakpoints.xl) return 'xl';
  if (width >= breakpoints.lg) return 'lg';
  if (width >= breakpoints.md) return 'md';
  if (width >= breakpoints.sm) return 'sm';
  return 'xs';
};

/**
 * Check if width matches breakpoint
 * @param {number} width - Window width
 * @param {string} breakpoint - Breakpoint name
 * @returns {boolean}
 */
export const isBreakpoint = (width, breakpoint) => {
  const breakpointValue = breakpoints[breakpoint];
  if (!breakpointValue) return false;
  return width >= breakpointValue;
};

/**
 * Helper for common mobile-first utility classes
 * Reduces repetitive responsive class writing
 */
export const mobileFirstClasses = {
  // Grid layouts - start with 1 col, scale up
  grid: {
    auto: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
    cols2: 'grid grid-cols-1 sm:grid-cols-2',
    cols3: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
    cols4: 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  },

  // Flex containers
  flex: {
    col: 'flex flex-col md:flex-row',
    row: 'flex flex-col sm:flex-row',
  },

  // Spacing - smaller on mobile
  padding: {
    card: 'p-3 sm:p-4 md:p-6',
    section: 'px-4 py-6 sm:px-6 md:px-8 md:py-8',
    input: 'px-3 py-2 sm:px-4 sm:py-3',
  },

  // Typography - responsive sizes
  text: {
    h1: 'text-xl sm:text-2xl md:text-3xl lg:text-4xl',
    h2: 'text-lg sm:text-xl md:text-2xl lg:text-3xl',
    h3: 'text-base sm:text-lg md:text-xl lg:text-2xl',
    body: 'text-sm sm:text-base md:text-base lg:text-lg',
    label: 'text-xs sm:text-sm md:text-sm',
  },

  // Responsive widths
  width: {
    full: 'w-full',
    auto: 'w-full sm:w-auto',
    maxContent: 'w-full max-w-[500px]',
  },

  // Touch targets - always min 44px on mobile
  touchTarget: 'min-h-[44px] min-w-[44px]',
  touchTargetLarge: 'min-h-[48px] min-w-[48px]',
};

/**
 * Combine responsive classes with conditions
 * @param {Object} classes - Object with breakpoint keys (mobile, tablet, desktop)
 * @returns {string} Combined Tailwind classes
 */
export const responsiveClasses = (classes) => {
  const { mobile = '', tablet = '', desktop = '' } = classes;
  return [mobile, `md:${tablet || mobile}`, `lg:${desktop || tablet || mobile}`]
    .filter(Boolean)
    .join(' ');
};

/**
 * Get device type from width
 * @param {number} width - Window width
 * @returns {Object} Device info { type, isMobile, isTablet, isDesktop }
 */
export const getDeviceType = (width) => {
  return {
    type: getBreakpointName(width),
    isMobile: width < breakpoints.md,
    isTablet: width >= breakpoints.md && width < breakpoints.lg,
    isDesktop: width >= breakpoints.lg,
  };
};

/**
 * Format screen size for readable display
 * @param {number} width - Window width
 * @param {number} height - Window height
 * @returns {string} Formatted screen size
 */
export const formatScreenSize = (width, height) => {
  const breakpoint = getBreakpointName(width);
  return `${width}x${height} (${breakpoint})`;
};

/**
 * Clamp value between min and max
 * Useful for responsive calculations
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export const clamp = (value, min, max) => {
  return Math.max(min, Math.min(max, value));
};

/**
 * Fluid typography - scale text based on viewport
 * Useful for responsive text sizes without Tailwind classes
 * @param {number} width - Window width
 * @param {number} minSize - Minimum font size (px)
 * @param {number} maxSize - Maximum font size (px)
 * @param {number} minWidth - Width at minimum size (px)
 * @param {number} maxWidth - Width at maximum size (px)
 * @returns {number} Calculated font size
 */
export const fluidTypography = (width, minSize, maxSize, minWidth = 320, maxWidth = 1440) => {
  const size = minSize + ((width - minWidth) / (maxWidth - minWidth)) * (maxSize - minSize);
  return clamp(size, minSize, maxSize);
};
